/**
 * The Class that is responsible for the drag and zoom controls for 3D apartment view.
 */
class DragControls {
  constructor(camera, renderer) {
    this.camera = camera;
    this.renderer = renderer;
    this.zoomOptions = {
      maxZoom: 2,
      minZoom: 5,
      step: 0.01,
    };
    this.minCameraZoom = 5;
    this.currentZoom = this.camera.position.z;
    this.cameraInitialPosition = {
      x: 0,
      y: 0,
      z: 5,
    };
    this.draggingObj = { isItDragging: false, positions: { x: 0, y: 0 } };
  }

  setAllEvents = () => {
    this.setZoomHandler();
    this.setDragCanvas();
    this.setCursorEvent();
  };

  setCursorEvent = () => {
    this.renderer.domElement.onmouseover = () => this.setCursor();
  };

  setCursor = () => {
    this.currentZoom < this.minCameraZoom
      ? (this.renderer.domElement.style.cursor = "all-scroll")
      : (this.renderer.domElement.style.cursor = "zoom-in");
  };

  setCameraToInitialPosition = () => {
    this.camera.position.x = this.cameraInitialPosition.x;
    this.camera.position.y = this.cameraInitialPosition.y;
    this.camera.position.z = this.cameraInitialPosition.z;
  };

  setDragCanvas = () => {
    this.renderer.domElement.onmousedown = (event) => {
      const { clientX, clientY } = event;
      this.draggingObj.isItDragging = true;
      const { x, y } = this.getWorldToCanvasABSCoordinates(clientX, clientY);
      this.draggingObj.positions = { x, y };
    };

    this.renderer.domElement.onmousemove = (event) => {
      if (this.draggingObj.isItDragging && this.currentZoom < this.minCameraZoom) {
        const { clientX, clientY } = event;
        const { x, y } = this.draggingObj.positions;
        const { x: newX, y: newY } = this.getWorldToCanvasABSCoordinates(
          clientX,
          clientY
        );
        this.draggingObj.positions = { x: newX, y: newY };
        this.camera.translateX(x - newX);
        this.camera.translateY(y - newY);
      }
    };

    this.renderer.domElement.onmouseup = () => {
      this.draggingObj.isItDragging = false;
    };
  };

  getWorldToCanvasABSCoordinates = (wx, wy) => {
    const { z } = this.camera.position;
    return this.getWorldToCanvasABSWithCustomZ(wx, wy, z);
  };

  setZoomHandler = () => {
    this.renderer.domElement.onwheel = (event) => {
      event.preventDefault();
      this.zoom(event);
    };
  };

  getWorldToCanvasPercentCoordinates = (wx, wy) => {
    const canvas = this.renderer.domElement;
    const percentX = (wx / canvas.clientWidth) * 2 - 1;
    const percentY = -(wy / canvas.clientHeight) * 2 + 1;
    return { percentX, percentY };
  };

  getABSBounds = () => {
    return this.getABSBoundsWithCustomZ(this.camera.position.z);
  };

  getWorldToCanvasCoordinates = (wx, wy) => {
    const { z } = this.camera.position;
    return this.getWorldToCanvasWithCustomZ(wx, wy, z);
  };

  getWorldToCanvasABSPercentCoordinates = (wx, wy) => {
    const canvas = this.renderer.domElement;
    const percentX = wx / canvas.clientWidth;
    const percentY = 1 - wy / canvas.clientHeight;
    return { percentX, percentY };
  };

  getWorldToCanvasABSWithCustomZ = (wx, wy, cz) => {
    const { percentX, percentY } = this.getWorldToCanvasABSPercentCoordinates(
      wx,
      wy
    );
    const { width, height } = this.getABSBoundsWithCustomZ(cz);
    const retX = width * percentX;
    const retY = height * percentY;
    return { x: retX, y: retY };
  };

  getWorldToCanvasWithCustomZ = (wx, wy, cz) => {
    const { percentX, percentY } = this.getWorldToCanvasPercentCoordinates(
      wx,
      wy
    );
    const { x, y } = this.camera.position;
    const { width, height } = this.getABSBoundsWithCustomZ(cz);
    const retX = x + (width / 2) * percentX;
    const retY = y + (height / 2) * percentY;
    return { x: retX, y: retY };
  };

  getABSBoundsWithCustomZ = (z) => {
    const fov = this.camera.fov;
    const aspect = this.camera.aspect;
    const halfHeight = Math.tan((fov * Math.PI) / 180 / 2) * z;
    const halfWidth = halfHeight * aspect;
    return { width: halfWidth * 2, height: halfHeight * 2 };
  };

  zoom = (event) => {
    const { clientX, clientY, deltaY } = event;
    const { step, minZoom, maxZoom } = this.zoomOptions;
    const { z: zp } = this.camera.position;
    let newZoom = zp;
    newZoom += deltaY * step;
    if (newZoom < maxZoom) {
      newZoom = maxZoom;
    } else if (newZoom > minZoom) {
      newZoom = minZoom;
    }

    if (newZoom === 5) this.setCameraToInitialPosition();

    const { x, y } = this.getWorldToCanvasCoordinates(clientX, clientY);
    const { x: futureX, y: futureY } = this.getWorldToCanvasWithCustomZ(
      clientX,
      clientY,
      newZoom
    );
    const offX = x - futureX;
    const offY = y - futureY;
    this.camera.translateX(offX * 0.2);
    this.camera.translateY(offY * 0.2);
    this.camera.position.setZ(newZoom);
    this.currentZoom = newZoom;
    this.setCursor();
  };
}
export default DragControls;
